import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import type { ArtistProfileResponse } from 'fym-shared/src/music24/artistProfile';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { assetOfItem, itemReasonLine, itemSubLine } from './sendAllItemFacts';
import { itemLabel } from './sendAllItemMessages';
import { sendAllMessages as s } from './sendAllMessages';
import type { SendAllItemView } from './sendAllModel';
import { SendAllChipBadge } from './SendAllChipBadge';
import { SendAllThumb } from './SendAllThumb';
import { withBrandWord } from '../../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Send to all sheet; each part uses its own slice of it */
import styles from './SendAll.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SendAllItemRowProps {
  view: SendAllItemView;
  profile: ArtistProfileResponse;
  ticked: boolean;
  /** Ticks are off while a send works. */
  locked: boolean;
  onToggle: (view: SendAllItemView, on: boolean) => void;
  onRetry: (view: SendAllItemView) => void;
  onReconnect: () => void;
  isSending: boolean;
}

/** One item of a service (design c `.it`): the tick, the preview, the name, the status and Retry. */
const SendAllItemRowComponent: React.FC<SendAllItemRowProps> = ({
  view,
  profile,
  ticked,
  locked,
  onToggle,
  onRetry,
  onReconnect,
  isSending,
}) => {
  const intl = useIntl();
  const label = intl.formatMessage(itemLabel(view.service, view.item));
  const reason = itemReasonLine(intl, view);
  const testId = `send-all-item-${view.key}`;

  return (
    <div className={clsx(styles.item, !view.canTick && !view.readOnly && styles.itemOff)} data-testid={testId}>
      <input
        type="checkbox"
        className={clsx(styles.tick, view.readOnly && styles.tickReadOnly)}
        checked={ticked}
        disabled={!view.canTick || locked}
        aria-label={label}
        onChange={(event) => onToggle(view, event.target.checked)}
        data-testid={`${testId}-tick`}
      />
      <SendAllThumb item={view.item} asset={assetOfItem(profile, view)} />
      <div className={styles.itemText}>
        <span className={styles.itemLabel}>{label}</span>
        <span className={styles.itemSub}>{itemSubLine(intl, view, profile)}</span>
        {reason !== null && (
          <span className={reason.isError ? styles.itemError : styles.itemSub} data-testid={`${testId}-reason`}>
            {withBrandWord(reason.text)}
          </span>
        )}
      </div>
      {view.chip !== null && <SendAllChipBadge kind={view.chip.kind} at={view.chip.at} testId={`${testId}-chip`} />}
      {view.retry === 'retry' && (
        <SMLButton className={styles.sm} disabled={isSending} onClick={() => onRetry(view)}>
          {intl.formatMessage(s.retry)}
        </SMLButton>
      )}
      {view.retry === 'reconnect' && (
        <SMLButton className={styles.sm} onClick={onReconnect}>
          {intl.formatMessage(s.reconnect)}
        </SMLButton>
      )}
    </div>
  );
};

export const SendAllItemRow = TypedMemo(SendAllItemRowComponent);
